import { A } from '@adornis/base/env-info.js';
import { ChemistryRootElement } from '@adornis/chemistry/chemistry-root-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { Router } from '@adornis/router/client/router.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import '@adornis/user-forms/x-login.js';
import '@dla/client/components/dla-login-nav.js';
import { UserPermission } from '@dla/db/User.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DlaHelpDialog } from './components/dla-help-dialog.js';
import './pages/dla-accessibility.js';
import './pages/dla-imprint';
import './pages/dla-privacy.js';
import './pages/dla-user-management.js';
import './pages/dla-writer.js';
import type { DlaTranslationDict } from './translations.js';

const version = require('../../package.json').version;

@customElement('main-element')
export class MainElement extends ChemistryRootElement {
  @state() _moodleMode = new RXController(this, routingState.value.searchParams.get('moodleMode'));

  private readonly _router = new Router(this, [
    {
      name: 'root',
      path: '/',
      render: () =>
        html`
          <x-flex padding="xxl" space="md" crossaxis-center>
            <x-button @click=${() => this._router.goTo('writer', { params: { id: A.getGloballyUniqueID() } })}>
              ${this._translator.translate('createNewPage')}
            </x-button>
            ${permissionGuard(
              UserPermission.UserManagement,
              () =>
                html`<x-button @click=${() => this._router.goTo('user-management')}>
                  ${this._translator.translate('userManagement')}
                </x-button>`,
            )}
          </x-flex>
        `,
    },
    { name: 'writer', path: '/writer/:id', render: ({ id }) => html` <dla-writer .rid=${id}></dla-writer> ` },
    {
      name: 'login',
      path: '/login',
      render: () => html`<x-flex padding="xxl"><x-login @login=${() => goTo('/')}></x-login></x-flex>`,
    },
    {
      name: 'user-management',
      path: '/user-management',
      render: () => html`<dla-user-management></dla-user-management>`,
    },
    {
      name: 'privacy',
      path: '/privacy',
      render: () => html` <dla-privacy></dla-privacy> `,
    },
    {
      name: 'imprint',
      path: '/imprint',
      render: () => html` <dla-imprint></dla-imprint> `,
    },
    { name: 'accessibility', path: '/accessibility', render: () => html` <dla-accessibility></dla-accessibility> ` },
  ]);

  private _translator = new TranslationController<DlaTranslationDict>(this);

  override render() {
    return html`
      <x-flex h100>
        <dla-login-nav></dla-login-nav>
        <x-flex ${css({ height: '100%', minHeight: '0' })}> ${this._router.outlet()} </x-flex>
        ${this._footer()}
      </x-flex>
    `;
  }

  private _helpButton() {
    if (this._moodleMode.value !== '1') {
      return nothing;
    }

    return html`
      <x-button
        mode="icon"
        icon="help"
        icon-outlined
        ${css({ color: this.colors.accent })}
        @click=${async () => {
          void DlaHelpDialog.showPopup({});
        }}
      >
        <x-tooltip> <x-text> ${this._translator.translate('help')} </x-text> </x-tooltip>
      </x-button>
    `;
  }

  private _footer() {
    return html`
      <x-flex
        horizontal
        center
        crossaxis-center
        space="lg"
        ${css({ height: this.sizes.footer, background: this.colors.neutral.shade(50) })}
      >
        <x-button
          mode="text"
          @click=${() => {
            goTo('/privacy' + (this._moodleMode.value === '1' ? '?moodleMode=1' : ''));
          }}
        >
          ${this._translator.translate('privacy')}
        </x-button>
        <x-button
          mode="text"
          @click=${() => {
            goTo('/imprint' + (this._moodleMode.value === '1' ? '?moodleMode=1' : ''));
          }}
        >
          ${this._translator.translate('imprint')}
        </x-button>
        <x-button
          mode="text"
          @click=${() => {
            goTo('/accessibility' + (this._moodleMode.value === '1' ? '?moodleMode=1' : ''));
          }}
        >
          ${this._translator.translate('accessibility')}
        </x-button>
        <x-text ${css({ opacity: 0.5 })}>Version ${version}</x-text>
        ${this._helpButton()}
      </x-flex>
    `;
  }
}
